'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import { v4 as uuid } from "uuid";
import { useTranslations } from 'next-intl';
import { FiX } from 'react-icons/fi';
import { RiRedPacketLine } from 'react-icons/ri';
import { TbTruckDelivery } from 'react-icons/tb';
import { usePathname, useRouter } from 'next/navigation';

import styles from './service-modal.module.scss';

import { alertService } from '_services/alert.service';
import { CompanyDetails, DayOfWeek, formatCurrency, OpeningHour, timezoneDateConvert } from '_services/common.service';
import { OrderServeDetails } from '@store/slices/orderActions';

import useStore from '@store/index';
import { CommonResponse, submitCollection, submitSets } from '_services/submit.service';
import { loadingService } from '_services/loading.service';
import { debounce } from 'lodash';

type DeliveryType = 'PICKUP' | 'DELIVERY'; // "SHIPMENT"
type TimeType = 'ASAP' | 'SCHEDULED';

export const ServiceModal = ({ 
    isManualShow, 
    onToggleManualShow,
    isCompanyOpen,
    currentDate 
}: { 
    isManualShow: boolean, 
    onToggleManualShow: () => void,
    isCompanyOpen: boolean | null,
    currentDate: Date | null 
}) => {
    const pathname = usePathname();
    const router = useRouter();
    const t = useTranslations();

    const isPickupEnabled = true; //companyDetails?.isPickupEnabled ?? true;
    const isDeliveryEnabled = true; //companyDetails?.isDeliveryEnabled ?? true;
   
    const [showServiceModal, setShowServiceModal] = React.useState(false);

    const [selectedService, setSelectedService] = useState<DeliveryType | null>('PICKUP');
    const [selectedTime, setSelectedTime] = useState<TimeType>('SCHEDULED');
    const [orderDate, setOrderDate] = useState<Date | null>(null);
    const [orderTime, setOrderTime] = useState<string | Date>('');
    
    const [isAfterClose, setIsAfterClose] = useState(false);
    const [isOnload, setIsOnload] = useState(true);
    const [currentServerTime, setCurrentServerTime] = useState<Date | null>(null);

    const [localCompanyDetails, setLocalCompanyDetails] = useState<CompanyDetails | null>(null);

    const [isShowCartClearConfirm, setIsShowCartClearConfirm] = useState(false);
    const [isSchedulePassed, setIsSchedulePassed] = useState(false);

    const [isFutureScheduleClosed, setIsFutureScheduleClosed] = useState(false);
    const [selectedScheduleTime, setSelectedScheduleTime] = useState('');

    const [currentCompanyOpen, setCurrentCompanyOpen] = useState<boolean | null>(null);

    const [isPickerTimesLoading, setIsPickerTimesLoading] = useState(false);

    // const [prevOrderClearConfirm, setPrevOrderClearConfirm] = useState(false);
    const pickerRef = React.useRef<DatePicker>(null);
    const datePickerRef = React.useRef<HTMLInputElement>(null);
    const timePickerRef = React.useRef<HTMLInputElement>(null);

    const [isSelectedDayClosed, setIsSelectedDayClosed] = useState(false);
    const timeGap = 15; //minutes

    // Get global states and actions from the store
    const { 
        orderServeDetails, setOrderServeDetails, 
        identityToken, setIdentityToken, setCompanyDetails,
        hasHydrated, cart, clearCart
    } = useStore();

    // Load company details on load
    const didMountRef = React.useRef(false);

    const fetchCompanyDetails = useCallback(async () => {
        const response = await submitSets(submitCollection.getCompanyDetails, { locationId: null }, false, null);
        if(response && response.status && response.extra){
            const companyDetails = response.extra as CompanyDetails;

            setLocalCompanyDetails(companyDetails);

            let isCompanyOpened = isCompanyOpen;

            const serverTime = new Date((companyDetails?.serverTime || new Date()));
            const isShopOpen = companyDetails?.shopStatus === 'OPEN';
            isCompanyOpened = isShopOpen;
            
            setCurrentCompanyOpen(isCompanyOpened);
            setCompanyDetails(companyDetails);
            setCurrentServerTime(serverTime);

            // const isShopOpen = await checkCompanyOpened(companyDetails);
            // console.log("Is shop open:", isShopOpen);
            setSelectedTime(isShopOpen ? 'ASAP' : 'SCHEDULED');

            const currentDay = serverTime.toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as DayOfWeek;
            const currentHour = serverTime.getHours();
            const currentMinute = serverTime.getMinutes();

            const openingHours = (companyDetails?.additionalInfo?.openingHours || []).find((day) => day.isOpen && day.day.toLowerCase() === currentDay.substring(0, 3)) as OpeningHour | undefined;
            // console.log(openingHours);
            
            if(openingHours){
                // Use local time to avoid timezone offset issues
                let convertedCloseTime = timezoneDateConvert({
                    utcDate: `${serverTime.getFullYear()}-${(serverTime.getMonth()+1).toString().padStart(2, '0')}-${serverTime.getDate().toString().padStart(2, '0')}`,
                    timezone: (localCompanyDetails?.timeZone || ''),
                    format: 'HH:mm'
                });
    
                (openingHours?.openingHours || []).sort((a, b) => {
                    const aStart = timezoneDateConvert({ utcDate: (a.startTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });
                    const bStart = timezoneDateConvert({ utcDate: (b.startTime || ''), timezone: localCompanyDetails?.timeZone, format: 'HH:mm' });
                    return (aStart < bStart ? -1 : 1);
                }).forEach((hourRange, index) => {
                    if((index + 1) === (openingHours?.openingHours || []).length){
                        convertedCloseTime = timezoneDateConvert({ utcDate: (hourRange.endTime || ''), timezone: (localCompanyDetails?.timeZone || ''), format: 'HH:mm' })
                    }
                });
    
                const [closeHour, closeMinute] = (convertedCloseTime || '').split(':').map(Number);
            
                // Check if current time is after closing
                const isAfterClose = (currentHour > closeHour || (currentHour === closeHour && currentMinute >= closeMinute));
                setIsAfterClose(isAfterClose);
            }
            
            if (orderServeDetails) {
                const currentOrderDetails = orderServeDetails;
                // console.log("Current order details:", currentOrderDetails);
            
                // Check if delivery scheduled time has already passed
                if(
                    (currentOrderDetails.fullFillType === "DELIVERY" && 
                    currentOrderDetails.deliveryDetails && 
                    currentOrderDetails.deliveryDetails.scheduleType === "SCHEDULED" &&
                    currentOrderDetails.deliveryDetails.deliveryAt !== '') ||

                    (currentOrderDetails.fullFillType === "PICKUP" && 
                    currentOrderDetails.pickupDetails && 
                    currentOrderDetails.pickupDetails.scheduleType === "SCHEDULED" &&
                    currentOrderDetails.pickupDetails.pickupAt !== '')
                ) {
                    const scheduledAt = new Date(currentOrderDetails.fullFillType === "DELIVERY" ? (currentOrderDetails?.deliveryDetails?.deliveryAt || '') : (currentOrderDetails?.pickupDetails?.pickupAt || ''));

                    // Compare scheduled time with new open hours to see if it's still valid
                    const scheduledDay = scheduledAt.toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as DayOfWeek;
                    const scheduledOpeningHours = (companyDetails?.additionalInfo?.openingHours || []).find((day) => day.isOpen && day.day.toLowerCase() === scheduledDay.substring(0, 3)) as OpeningHour | undefined;

                    if(scheduledOpeningHours){
                        let isWithinOpenHours = false;
                        (scheduledOpeningHours?.openingHours || []).forEach((hourRange) => {
                            // Use local time to avoid timezone offset issues
                            const convertedOpenTime = timezoneDateConvert({ utcDate: (hourRange?.startTime || ''), timezone: (localCompanyDetails?.timeZone || ''), format: 'HH:mm' });
                            const convertedCloseTime = timezoneDateConvert({ utcDate: (hourRange?.endTime || ''), timezone: (localCompanyDetails?.timeZone || ''), format: 'HH:mm' });
                            const [openHour, openMinute] = (convertedOpenTime || '').split(':').map(Number);
                            const [closeHour, closeMinute] = (convertedCloseTime || '').split(':').map(Number);
                            if(
                                (scheduledAt.getHours() > openHour || (scheduledAt.getHours() === openHour && scheduledAt.getMinutes() >= openMinute)) &&
                                (scheduledAt.getHours() < closeHour || (scheduledAt.getHours() === closeHour && scheduledAt.getMinutes() < closeMinute))
                            ){
                                isWithinOpenHours = true;
                            }
                        });

                        if(!isWithinOpenHours){
                            setIsSchedulePassed(true);
                            setIsFutureScheduleClosed(true);
                            setSelectedScheduleTime(timezoneDateConvert({ utcDate: scheduledAt.toISOString(), timezone: (localCompanyDetails?.timeZone || ''), format: 'YYYY-MM-DD HH:mm a' }).toString());
                            loadingService.loading(false);
                            
                            if(cart && cart.lineItems && cart.lineItems.length > 0){
                                setIsShowCartClearConfirm(true);
                                setShowServiceModal(false);
                            } else{
                                setOrderServeDetails(null);
                                clearCart();
                                setShowServiceModal(true);
                            }
                            return;
                        }
                    }

                    // before current server time
                    if (scheduledAt < serverTime) {
                        setIsSchedulePassed(true);

                        if(cart && cart.lineItems && cart.lineItems.length > 0){
                            setIsShowCartClearConfirm(true);
                            setShowServiceModal(false);
                        } else{
                            setOrderServeDetails(null);
                            clearCart();
                            setShowServiceModal(true);
                        }

                        return;
                    }
                }

                // If shop is closed and order is ASAP, clear order details
                if (
                    !isShopOpen && 
                    ((currentOrderDetails.fullFillType === "DELIVERY" && currentOrderDetails.deliveryDetails && currentOrderDetails.deliveryDetails.scheduleType === "ASAP") ||
                    (currentOrderDetails.fullFillType === "PICKUP" && currentOrderDetails.pickupDetails && currentOrderDetails.pickupDetails.scheduleType === "ASAP"))
                ) {
                    setIsSchedulePassed(false);

                    if(cart && cart.lineItems && cart.lineItems.length > 0){
                        setIsShowCartClearConfirm(true);
                        setShowServiceModal(false);
                    } else{
                        setOrderServeDetails(null);
                        clearCart();
                        setShowServiceModal(true);
                    }

                    // if(cart.orderId){
                    //     setPrevOrderClearConfirm(true);
                    // }

                    return;
                }

                // If fullfilledAt is set, check if it's not today
                if (currentOrderDetails && currentOrderDetails.setAt) {
                    const setAt = new Date(currentOrderDetails.setAt);

                    //check not today
                    if (setAt.toDateString() !== serverTime.toDateString()) {
                        setOrderServeDetails(null);
                        clearCart();
                        setShowServiceModal(true);

                        // if(cart.orderId){
                        //     setPrevOrderClearConfirm(true);
                        // }
                    }
                }

                // If ASAP order is not today, clear order details
                if (
                    ((currentOrderDetails.fullFillType === "DELIVERY" && currentOrderDetails.deliveryDetails && currentOrderDetails.deliveryDetails.scheduleType === "ASAP") ||
                    (currentOrderDetails.fullFillType === "PICKUP" && currentOrderDetails.pickupDetails && currentOrderDetails.pickupDetails.scheduleType === "ASAP")) &&
                    (new Date(currentOrderDetails.setAt).toDateString() !== serverTime.toDateString())
                ) {
                    setOrderServeDetails(null);
                    clearCart();
                    setShowServiceModal(true);

                    // if(cart.orderId){
                    //     setPrevOrderClearConfirm(true);
                    // }

                    return;
                }

                // console.log("Order serve details are valid.");
                // setShowServiceModal(false);

                if(isManualShow){
                    setSelectedService((orderServeDetails?.fullFillType?.toUpperCase?.() === 'DELIVERY' ? 'DELIVERY' : 'PICKUP') as DeliveryType);
                    setSelectedTime((orderServeDetails?.pickupDetails?.scheduleType === 'ASAP' || orderServeDetails?.deliveryDetails?.scheduleType === 'ASAP') ? 'ASAP' : 'SCHEDULED');

                    if(orderServeDetails?.pickupDetails?.scheduleType === 'SCHEDULED' && orderServeDetails?.pickupDetails?.pickupAt){
                        setOrderDate(new Date(orderServeDetails?.pickupDetails?.pickupAt));
                        setOrderTime(new Date(orderServeDetails?.pickupDetails?.pickupAt).toTimeString().substring(0, 5));
                    }

                    if(orderServeDetails?.deliveryDetails?.scheduleType === 'SCHEDULED' && orderServeDetails?.deliveryDetails?.deliveryAt){
                        setOrderDate(new Date(orderServeDetails?.deliveryDetails?.deliveryAt));
                        setOrderTime(new Date(orderServeDetails?.deliveryDetails?.deliveryAt).toTimeString().substring(0, 5));
                    }

                    loadingService.loading(false);
                }

            } else {
                setShowServiceModal(true);
                loadingService.loading(false);
            }

        }

    }, []);

    useEffect(() => {
        if (didMountRef.current) return;
        didMountRef.current = true;
        fetchCompanyDetails();
    }, []);

    // Generate and set unique identity token for the user if not already set
    useEffect(() => {
        // console.log(identityToken);
        if (!identityToken) {
            // Generate unique identity token only for this user that never be duplicated
            const token = uuid()+'-'+new Date().getTime();

            setIdentityToken(token);
        }
    }, [identityToken, setIdentityToken]);

    // State for allowed times based on opening hours
    const [allowedTimes, setAllowedTimes] = useState<Date[]>([]);
    
    const roundUpToSlot = (date: Date, gapMinutes: number) => {
        const ms = gapMinutes * 60 * 1000;
        return new Date(Math.ceil(date.getTime() / ms) * ms);
    }

    const fetchAvailableTimes = async () => {
        if(!orderDate){
            setAllowedTimes([]);
            return;
        }

        if (!localCompanyDetails || !localCompanyDetails.additionalInfo || !localCompanyDetails.additionalInfo.openingHours) {
            return;
        }
        
        const currentDay = new Date(orderDate || '').toLocaleString('en-US', { weekday: 'long' }).toLowerCase() as keyof NonNullable<typeof localCompanyDetails.additionalInfo.openingHours>[number]['day'];
        // let isOpenToday = false;
        
        // Calculate allowed times based on current day opening hours
        const openingHours = (localCompanyDetails.additionalInfo.openingHours || []).find((day) => day.isOpen && day.day.toLowerCase() === (currentDay as string).substring(0, 3)) as OpeningHour | undefined;
        // console.log(openingHours);
        
        if (openingHours) {
            setIsSelectedDayClosed(false);

            //current hour and minute
            let currentTime = new Date((currentDate ? currentDate.toISOString() : new Date().toISOString()));

            setIsPickerTimesLoading(true);
            loadingService.loading(true);
            const servereTimeResponse: CommonResponse = await submitSets(submitCollection.loadServerTime, {
                locationId: (localCompanyDetails?.locationId || "")
            }, false, null);

            if (
                servereTimeResponse &&
                servereTimeResponse.status &&
                servereTimeResponse.extra &&
                Object.keys(servereTimeResponse.extra).length > 0
            ) {
                const respTimeString = (servereTimeResponse.extra as { serverTime?: string }).serverTime || '';
                currentTime = new Date(timezoneDateConvert({ utcDate: respTimeString, timezone: localCompanyDetails.timeZone, format: 'YYYY-MM-DD HH:mm:ss' }));
                // console.log("Server time:", currentTime);

                // isOpenToday = (servereTimeResponse.extra as { shopStatus?: string }).shopStatus === 'OPEN';
                setCurrentServerTime(currentTime);

                setCompanyDetails({
                    ...localCompanyDetails,
                    serverTime: currentTime.toISOString(),
                } as CompanyDetails);
            }

            loadingService.loading(false);
            
            // const changeCurrentTime = new Date(currentTime.setMinutes(currentTime.getMinutes() + timeGap));
            const currentHour = currentTime.getHours();
            const currentMinute = currentTime.getMinutes();
            
            // Always set to midnight first to avoid time drift
            const setStartDate = new Date(orderDate?.getTime() || currentTime.getTime());
            setStartDate.setHours(0, 0, 0, 0);

            const times: Date[] = [];
            let isCurrentlyClosed = false;

            (openingHours?.openingHours || []).sort((a, b) => {
                const aStart = timezoneDateConvert({ utcDate: (a.startTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });
                const bStart = timezoneDateConvert({ utcDate: (b.startTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });
                return (aStart < bStart ? -1 : 1);
            }).forEach((hourRange, index) => {
                // openingHours.open and openingHours.close are in 'HH:mm' format, e.g., '11:00', '23:00'
                // Use local time to avoid timezone offset issues
                const convertedOpenTime = timezoneDateConvert({ utcDate: (hourRange?.startTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });
                const convertedCloseTime = timezoneDateConvert({ utcDate: (hourRange?.endTime || ''), timezone: localCompanyDetails.timeZone, format: 'HH:mm' });

                const [openHour, openMinute] = (convertedOpenTime || '').split(':').map(Number);
                const [closeHour, closeMinute] = (convertedCloseTime || '').split(':').map(Number);

                // If the selected date is today, set the start time to the next available slot
                if (orderDate && new Date(orderDate).toDateString() === new Date(currentTime).toDateString()) {

                    // If current time is before opening
                    // console.log(currentHour, openHour, currentMinute, openMinute);

                    if (index === 0 && (currentHour < openHour || (currentHour === openHour && currentMinute < openMinute))) {
                        // if(!isOnload){
                        //     alertService.info(t('serviceModal.chooseTimeBetween', { openHour: openHour.toString().padStart(2, '0'), openMinute: openMinute.toString().padStart(2, '0') }), 3000, true);
                        // }

                        setStartDate.setHours(openHour, (openMinute + timeGap), 0, 0);
                    } else{
                        if((index + 1) === (openingHours?.openingHours || []).length && (currentHour > closeHour || (currentHour === closeHour && currentMinute >= closeMinute))){
                            if(!isOnload){
                                alertService.info(t('serviceModal.shopOutOfHours'), 3000, true);
                            }
                            isCurrentlyClosed = true;
                        }
                    }

                    if (currentHour > openHour || (currentHour === openHour && currentMinute > openMinute)) {
                        // Round up to the next 15 minute interval
                        const roundedMinutes = Math.ceil(currentMinute / 15) * 15;
                        setStartDate.setHours(currentHour, roundedMinutes, 0, 0);

                        // If the rounded minutes exceed 60, adjust the hour and minutes accordingly
                        if (roundedMinutes >= 60) {
                            setStartDate.setHours(currentHour + 1, 0, 0, 0);
                        }
                    } else {
                        setStartDate.setHours(openHour, openMinute, 0, 0);
                    }
                } else {
                    setStartDate.setHours(openHour, openMinute, 0, 0);
                }
                
                const setEndDate = new Date(orderDate?.getTime() || Date.now());
                setEndDate.setHours(0, 0, 0, 0);
                setEndDate.setHours(closeHour, closeMinute, 0, 0);

                const startTime = new Date(setStartDate.getTime()); // add time gap to start time
                const endTime = new Date((setEndDate.getTime() - timeGap * 60000)); // subtract time gap from end time
                
                // Use local time for all time slots
                const slot = roundUpToSlot(startTime, timeGap);
                
                while (slot <= endTime) {
                    times.push(new Date(slot.getTime()));
                    slot.setMinutes(slot.getMinutes() + 15);
                }
                
            });

            if(isCurrentlyClosed){
                setAllowedTimes([]);
                setIsPickerTimesLoading(false);
                return;
            }

            setAllowedTimes(times);

            if(!isOnload){
                if(orderTime){
                    setOrderTime(''); // Reset order time when date changes
                }
            }
            
            setIsOnload(false);
            setIsPickerTimesLoading(false);

        } else{
            alertService.error(t('serviceModal.shopClosedDate', { closedDate: timezoneDateConvert({ utcDate: orderDate.toISOString(), timezone: (localCompanyDetails?.timeZone || ''), format: 'YYYY-MM-DD' }) }), 3000, true);
            setAllowedTimes([]);
            setIsSelectedDayClosed(true);
        }
    }

    // Debounce fetchAvailableTimes to avoid excessive calls
    const fetchAvailableTimesDebounced = useCallback(debounce(fetchAvailableTimes, 300), [orderDate, localCompanyDetails]);

    useEffect(() => {
        if(!orderDate || !localCompanyDetails) return;

        fetchAvailableTimesDebounced();
    }, [orderDate, localCompanyDetails]);

    const handleServiceSelect = (service: DeliveryType) => {
        setSelectedService(service);
    };

    const handleTimeSelect = (time: TimeType) => {
        setSelectedTime(time);
    };

    const handleSaveServeDetails = async () => {
        //validate inputs
        if (!selectedService) {
            alertService.error(t('serviceModal.selectServiceType'), 3000, true);
            return;
        }
        
        if (selectedTime === 'SCHEDULED' && (!orderDate || !orderTime)) {
            alertService.error(t('serviceModal.selectDateTime'), 3000, true);
            return;
        }

        let isNowOpen = false;
        
        loadingService.loading(true);
        const servereTimeResponse: CommonResponse = await submitSets(submitCollection.loadServerTime, {
            locationId: (localCompanyDetails?.locationId || "")
        }, false, null);

        let serverTime = new Date((currentDate ? currentDate : new Date().toISOString()));
        if (
            servereTimeResponse &&
            servereTimeResponse.status &&
            servereTimeResponse.extra &&
            Object.keys(servereTimeResponse.extra).length > 0
        ) {
            serverTime = new Date(timezoneDateConvert({ utcDate: (servereTimeResponse.extra as { serverTime?: string }).serverTime || '', timezone: localCompanyDetails?.timeZone || 'UTC', format: 'YYYY-MM-DD HH:mm:ss' }));
            isNowOpen = (servereTimeResponse.extra && (servereTimeResponse.extra as { shopStatus?: string }).shopStatus === 'OPEN') as boolean;

            setCurrentServerTime(serverTime);
            setCompanyDetails({
                ...localCompanyDetails,
                serverTime: serverTime.toISOString(),
            } as CompanyDetails);
        }

        loadingService.loading(false);
        
        // If company is closed, force to scheduled
        if (!isNowOpen && selectedTime === 'ASAP') {
            alertService.error(t('serviceModal.shopOutOfHours'), 3000, true);
            return;
        }

        // If scheduled time is selected, ensure date and time are valid
        if (selectedTime === 'SCHEDULED') {
            if (!orderDate) {
                alertService.error(t('serviceModal.selectScheduledDate'), 3000, true);
                return;
            }
            if (!orderTime) {
                alertService.error(t('serviceModal.selectScheduledTime'), 3000, true);
                return;
            }

            // Ensure selected date is not in the past
            const today = serverTime;
            today.setHours(0, 0, 0, 0);
            
            const selectedDate = new Date(orderDate);
            selectedDate.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                alertService.error(t('serviceModal.scheduledDateInPast'), 3000, true);
                return;
            }
        }

        //merge date and time into a single string
        let orderDateTime: Date | undefined = undefined;
        if (orderDate && orderTime) {
            // If orderTime is a Date object, extract hours and minutes from it
            let hours: number, minutes: number;
            if (orderTime && typeof orderTime === 'object' && orderTime instanceof Date) {
                hours = (orderTime as Date).getHours();
                minutes = (orderTime as Date).getMinutes();
            } else if (typeof orderTime === 'string') {
                [hours, minutes] = orderTime.split(':').map(Number);
            } else {
                hours = 0;
                minutes = 0;
            }
            orderDateTime = new Date(orderDate);
            orderDateTime.setHours(hours, minutes, 0, 0);
        }

        if (
            selectedTime === 'SCHEDULED' &&
            orderDate &&
            orderDateTime &&
            new Date(orderDateTime) < new Date(serverTime)
        ) {
            alertService.error(t('serviceModal.scheduledDateInPast'), 3000, true);
            return;
        }


        // Prepare the details object
        const details = {
            fullFillType: selectedService,
            pickupDetails: selectedService === 'PICKUP' ? {
                recipient: {
                    displayName: "",
                    emailAddress: "",
                    phoneNumber: "",
                    address: null
                },
                scheduleType: selectedTime,
                pickupAt: selectedTime === 'ASAP' ? null : orderDateTime,
                note: "",
            } : null,
            deliveryDetails: selectedService === 'DELIVERY' ? {
                recipient: {
                    displayName: "",
                    emailAddress: "",
                    phoneNumber: "",
                    address: {
                        addressLine1: "",
                        apartmentNo: "",
                        locality: "", //city
                        administrativeDistrictLevel1: "", //state
                        country: ""
                    }
                },
                scheduleType: selectedTime,
                deliveryAt: selectedTime === 'ASAP' ? null : orderDateTime,
                note: "",
            } : null,
            setAt: new Date(serverTime),
        } as OrderServeDetails;
        
        setOrderServeDetails(details);
        
        if(isManualShow){
            onToggleManualShow();
        } else{
            setShowServiceModal(false);
        }
    };

    const changeTime = (date: Date | null) => {
        // console.log("change time", date);

        if (date) {
            const currentOrderDate = new Date(orderDate?.getTime() || Date.now());
            currentOrderDate.setHours(date.getHours(), date.getMinutes(), 0, 0);

            const timeString = date.toTimeString().substring(0, 5); // HH:mm format
            setOrderTime(timeString);
        } else {
            setOrderTime('');
        }
    };

    const getNextOpenTime = (): Date => {
        const currentTime = new Date((currentDate ? currentDate : new Date().toISOString()));
        const nextOpenTime = new Date(currentTime.setDate(currentTime.getDate() + 1)); // Start with tomorrow
        return nextOpenTime;
    };

    const handleClearCart = () => {
        clearCart();
        setOrderServeDetails(null);
        setIsShowCartClearConfirm(false);
        setIsSchedulePassed(false);

        setTimeout(() => {
            if(pathname.includes('cart') || pathname.includes('checkout')){
                router.push('/menu/all');
            } else{
                setSelectedService('PICKUP');
                setSelectedTime((currentCompanyOpen ? 'ASAP' : 'SCHEDULED'));
                setOrderDate(null);
                setOrderTime('');
                setShowServiceModal(true);
            }    
        }, 300);
    };

    // console.log(allowedStartTime, allowedEndTime);

    const ranges = (localCompanyDetails?.paymentLimits?.ranges?.ranges || []) as Array<{ rangeEnd?: number; amount?: number; currency?: string; rangeUnit?: string }>;
    const minRangeObj = ranges.length > 0
        ? ranges.reduce((min, obj) => ((obj?.rangeEnd ?? 0) < (min?.rangeEnd ?? 0) ? obj : min))
        : { rangeEnd: 0, amount: 0, currency: 'USD', rangeUnit: 'km' };
    const maxRangeObj = ranges.length > 0
        ? ranges.reduce((max, obj) => ((obj?.rangeEnd ?? 0) > (max?.rangeEnd ?? 0) ? obj : max))
        : { rangeEnd: 0, amount: 0, currency: 'USD', rangeUnit: 'km' };

    // console.log(minRangeObj, maxRangeObj);
    
    if(!hasHydrated){
        return null;
    }

    return (<>
        <Modal show={(!isShowCartClearConfirm && isManualShow) || showServiceModal} centered className={styles.modal} backdrop="static" keyboard={false}>
            {isManualShow && <button className={styles.closeBtn} onClick={onToggleManualShow}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1L9 9M9 1L1 9" stroke="#101010" strokeWidth="2" strokeLinecap="round"/>
                </svg>
            </button>}
            
            <Modal.Body className={styles.modalBody}>
                <h2>{isFutureScheduleClosed ? t('serviceModal.futureScheduleClosedMessage') : isSchedulePassed ? t('serviceModal.clearCartTitleSchedule') : currentCompanyOpen ? t('serviceModal.chooseService'): t('serviceModal.closedMessage')}</h2>
                <p className={styles.subtitle}>{ isFutureScheduleClosed ? t('serviceModal.futureScheduleClosedSubtitle', { scheduleTime: selectedScheduleTime }) : isSchedulePassed ? t('serviceModal.timePassedMessageSchedule') : currentCompanyOpen ? t('serviceModal.chooseOrderMethod') : t('serviceModal.closedSubtitle')}</p>

                <div className={styles.serviceButtons}>
                    {/* companyDetails.isPickupEnabled &&  */}
                    {isPickupEnabled && <button
                        className={`${styles.serviceButton} ${selectedService === 'PICKUP' ? styles.active : ''}`}
                        onClick={() => handleServiceSelect('PICKUP')}
                        id='pickup-service-button'
                    >
                        <RiRedPacketLine size={20} />
                        <span>{t('serviceModal.pickup')}</span>
                    </button>}

                    {/* companyDetails.isDeliveryEnabled &&  */}
                    {isDeliveryEnabled && <button
                        className={`${styles.serviceButton} ${selectedService === 'DELIVERY' ? styles.active : ''}`}
                        onClick={() => handleServiceSelect('DELIVERY')}
                        id='delivery-service-button'
                    >
                        <TbTruckDelivery size={24} />
                        <span>{t('serviceModal.delivery')}</span>
                    </button>}
                </div>

                {currentCompanyOpen && <div className={styles.timeSelector}>
                <button
                    id="asap-time-button"
                    className={`${styles.timeButton} ${selectedTime === 'ASAP' ? styles.active : ''}`}
                    onClick={() => handleTimeSelect('ASAP')}
                >
                    {t('serviceModal.now')}
                </button>
                <button
                    id="scheduled-time-button"
                    className={`${styles.timeButton} ${selectedTime === 'SCHEDULED' ? styles.active : ''}`}
                    onClick={() => handleTimeSelect('SCHEDULED')}
                >
                    {t('serviceModal.later')}
                </button>
                </div>}

                {(!currentCompanyOpen || selectedTime === 'SCHEDULED') && (
                <div className={styles.dateTimeSelector}>
                    <div className={styles.inputGroup}>
                        <label>{t('Checkout.DATE')}</label>
                        <DatePicker
                            ref={pickerRef}
                            id="order-date-picker"
                            selected={orderDate ? new Date(orderDate) : null}
                            disabled={isPickerTimesLoading}
                            onKeyDown={(e) => e.preventDefault()}
                            onChange={(date: Date | null) => {
                                if (date) {
                                    setOrderDate(new Date(date));
                                    setOrderTime('');
                                } else {
                                    setOrderDate(null);
                                }
                            }}
                            minDate={isAfterClose ? new Date(getNextOpenTime()) : (currentServerTime ? currentServerTime : new Date())} // Set minimum date to today or next open day
                            maxDate={new Date(new Date().setDate(new Date().getDate() + 7))} // max 7 days ahead
                            dateFormat="yyyy-MM-dd"
                            placeholderText={t('Checkout.SELECT_DATE')}
                            autoComplete='off'
                            shouldCloseOnSelect={true}
                            customInput={<Form.Control 
                                type='text' 
                                id='order-date-input'
                                value={orderDate ? orderDate.toISOString().split('T')[0] : ''} 
                                readOnly 
                                ref={datePickerRef} 
                                inputMode='none'
                                style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                />}
                        />

                        {orderDate && <span className={styles.clearOption} onClick={() => {
                            setOrderDate(null);
                            setOrderTime('');
                            setIsSelectedDayClosed(false);
                        }} title={t('common.clear')}><FiX /></span>}
                        
                    </div>

                    {isSelectedDayClosed && <p className={styles.closedNote}>{t('serviceModal.closedForTheDay')}</p>}

                    {!isSelectedDayClosed && <div className={styles.inputGroup}>
                        <label>{t('Checkout.TIME')}</label>
                        <DatePicker
                            id="order-time-picker"
                            selected={orderTime ? new Date(`1970-01-01T${orderTime}:00`) : null}
                            onKeyDown={(e) => e.preventDefault()}
                            onChange={(date: Date | null) => {
                                changeTime(date);
                            }}
                            onCalendarOpen={() => {
                                if(allowedTimes.length > 0 && !orderTime){
                                    setTimeout(() => {
                                        const list = document.querySelector(".react-datepicker__time-list");
                                        if (list) {
                                            list.scrollTop = 0;
                                        }
                                    }, 50);
                                }
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption={t('Checkout.TIME')}
                            dateFormat="hh:mm aa"
                            placeholderText={t('Checkout.SELECT_TIME')}
                            includeTimes={allowedTimes}
                            autoComplete='off'
                            customInput={<Form.Control 
                                type='text' 
                                value={orderTime ? (typeof orderTime === 'string' ? orderTime : orderTime.toTimeString().substring(0, 5)) : ''}
                                readOnly 
                                ref={timePickerRef} 
                                inputMode='none'
                                style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                />}
                            // excludeTimes={excludedTimes}
                        />

                        {orderTime && <span className={styles.clearOption} onClick={() => setOrderTime('')} title={t('common.clear')}><FiX /></span>}
                    </div>}
                </div>
                )}

                <div id="btn-options" className={styles.btnOptions}>
                    <Button
                        id="fulfill-service-button"
                        className={styles.continueButton}
                        disabled={!selectedService || (selectedTime === 'SCHEDULED' && (!orderDate || !orderTime))}
                        onClick={handleSaveServeDetails}
                    >
                        {currentCompanyOpen ? t('serviceModal.continue') : t('serviceModal.placeFutureOrder')}
                    </Button>
                </div>

                {selectedService && <p className={styles.note}>
                {selectedService === 'DELIVERY' ? 
                    t('serviceModal.deliveryNote', { 
                        minCharge: formatCurrency((minRangeObj?.amount || 0), (minRangeObj?.currency || 'USD')),
                        maxCharge: formatCurrency((maxRangeObj?.amount || 0), (maxRangeObj?.currency || 'USD')),
                        minDistance: (minRangeObj?.rangeEnd || 0) + (minRangeObj?.rangeUnit || 'km'),
                        maxDistance: (localCompanyDetails?.paymentLimits?.allowDeliveryRanges?.max || 0) + (localCompanyDetails?.paymentLimits?.allowDeliveryRanges?.unit || 'km'),
                        minOrderAmount: formatCurrency((localCompanyDetails?.paymentLimits?.orderValueDeliveryLimit?.amount || 0), (localCompanyDetails?.paymentLimits?.orderValueDeliveryLimit?.currency || 'USD'))
                    }) :
                    t('serviceModal.pickupNote')}
                </p>}
            </Modal.Body>
        </Modal>

        <Modal show={isShowCartClearConfirm} centered className={styles.modal + ' ' + styles.rescheduleOrdermodal} backdrop="static" keyboard={false}>
            <Modal.Body className={styles.modalBody}>
                <h2 className={styles.modalHeader}>{t((isFutureScheduleClosed ? 'serviceModal.futureScheduleClosedMessage' : isSchedulePassed ? 'serviceModal.clearCartTitleSchedule' : 'serviceModal.clearCartTitle'))}</h2>
                <p className={styles.subtitle}>{t((isFutureScheduleClosed ? 'serviceModal.futureScheduleClearSubtitle' : isSchedulePassed ? 'serviceModal.clearCartMessageSchedule' : 'serviceModal.clearCartMessage'), { scheduleTime: selectedScheduleTime })}</p>
                <div className={styles.btnGroup}>
                    <Button variant="secondary" onClick={() => handleClearCart()}>
                        {t('common.clear')}
                    </Button>
                    <Button variant="danger" onClick={() => {
                        setIsShowCartClearConfirm(false);
                        setShowServiceModal(true);
                    }}>
                        {t('serviceModal.keepAndReschedule')}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    </>);
};

export default ServiceModal;
